import React from 'react';
import PropTypes from 'prop-types';

// Output Battle item metadata for leaderboard rows — mirrors the game's BOONS /
// LOOT_ITEMS catalogs (game-prototype/public/play/index.html, backend
// lootTables.ts). Keep in sync when new boons/weapons ship. Icons are the
// game's own sprites, served from the frontend's static asset folder.
const ASSET_BASE = '/output-battle/assets';

export const OB_ITEMS = {
    // boons
    heal: { jp: '回復', en: 'Health Potion', desc: 'Restore HP to full.', icon: 'boon-heal' },
    power: { jp: '力', en: 'Power', desc: '+20% attack damage. Stacks.', icon: 'boon-power' },
    vitality: { jp: '活力', en: 'Vitality', desc: '+25 max HP and heal it. Stacks.', icon: 'boon-vitality' },
    shield: { jp: '盾', en: 'Shield', desc: 'Block the next enemy hit. Refills every boss.', icon: 'boon-shield' },
    thorns: { jp: '反射', en: 'Thorns', desc: 'Reflect 8 damage when the oni hits you. Stacks.', icon: 'boon-thorns' },
    secondwind: { jp: '不死', en: 'Second Wind', desc: 'Survive one lethal blow at 1 HP.', icon: 'boon-secondwind' },
    fervor: { jp: '熱狂', en: 'Fervor', desc: 'Combo cap +1 (×3→×4). Stacks.', icon: 'boon-fervor' },
    insight: { jp: '洞察', en: 'Insight', desc: "Reveal each grammar pattern's structure.", icon: 'boon-insight' },
    fortune: { jp: '幸運', en: 'Fortune', desc: '+15% score earned. Stacks.', icon: 'boon-fortune' },
    tempo: { jp: '時間', en: 'Time Candle', desc: '+5s answer time on every turn. Stacks.', icon: 'boon-tempo' },
    // weapons
    w_iron: { jp: '鉄の剣', en: 'Iron Sword', desc: '+8% damage this run.', icon: 'weapon-iron' },
    w_longsword: { jp: '長剣', en: 'Longsword', desc: '+12% damage this run.', icon: 'weapon-longsword' },
    w_axe: { jp: '戦斧', en: 'Battle Axe', desc: '+14% damage this run.', icon: 'weapon-axe' },
    w_katana: { jp: '刀', en: 'Katana', desc: '+16% damage this run.', icon: 'weapon-katana' },
    w_hammer: { jp: '戦槌', en: 'War Hammer', desc: '+18% damage this run.', icon: 'weapon-hammer' },
    w_staff: { jp: '魔杖', en: 'Arcane Staff', desc: '+15% damage this run.', icon: 'weapon-staff' },
    w_poison: { jp: '毒の短剣', en: 'Poison Dagger', desc: '+20% damage this run.', icon: 'weapon-poison' },
    w_flame: { jp: '炎の剣', en: 'Flame Blade', desc: '+25% damage this run.', icon: 'weapon-flame' },
    w_ice: { jp: '氷の剣', en: 'Ice Blade', desc: '+25% damage this run.', icon: 'weapon-ice' },
    w_scythe: { jp: '大鎌', en: 'Reaper Scythe', desc: '+30% damage this run.', icon: 'weapon-scythe' },
    // cursed weapons — the flex tier
    c_rust: { jp: '錆びた刃', en: 'Rusted Cleaver', desc: '−20% damage for 5 attacks. A cursed blade with no upside.', icon: 'weapon-cursed-rust', cursed: true },
    c_siphon: { jp: '吸血の刃', en: 'Siphon Blade', desc: '−10% damage, but your hits heal you (4 attacks).', icon: 'weapon-cursed-siphon', cursed: true },
    c_offering: { jp: '供物の剣', en: 'Offering Cleaver', desc: '+10% damage, but your hits also heal the boss (3 attacks).', icon: 'weapon-cursed-cleaver', cursed: true },
    c_glass: { jp: 'ガラスの剣', en: 'Glass Sword', desc: '+30% damage for 2 attacks, then it shatters.', icon: 'weapon-cursed-glass', cursed: true },
};

function ItemPip({ id, count }) {
    const meta = OB_ITEMS[id];
    if (!meta) return null;
    return (
        <span className="relative group inline-flex">
            <span
                className={`relative inline-flex items-center justify-center w-7 h-7 rounded-lg border p-0.5 ${
                    meta.cursed ? 'border-purple-400 bg-purple-950' : 'border-gray-300 bg-white'
                }`}
            >
                <img
                    src={`${ASSET_BASE}/${meta.icon}.webp`}
                    alt={meta.en}
                    className="w-full h-full object-contain"
                    loading="lazy"
                />
                {count > 1 && (
                    <span className="absolute -bottom-1 -right-1 bg-yellow-400 text-gray-900 text-[9px] font-black rounded-full px-1 leading-3">
                        {count}
                    </span>
                )}
            </span>
            {/* in-game style tooltip */}
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-max max-w-[220px] rounded-lg border-2 border-yellow-400 bg-slate-900 px-3 py-2 text-left shadow-xl">
                <span className="block text-yellow-400 text-[11px] font-bold tracking-wide">
                    {meta.jp} {meta.en}
                    {count > 1 ? ` ×${count}` : ''}
                </span>
                <span className="block text-amber-50 text-xs font-medium leading-snug">{meta.desc}</span>
            </span>
        </span>
    );
}

ItemPip.propTypes = {
    id: PropTypes.string.isRequired,
    count: PropTypes.number,
};

// The run's kit as a compact icon strip: final weapon first, then boons.
// items = { boons: {boonId: count}, weapon: lootItemId | null }
function OutputBattleItemStrip({ items }) {
    const boons = (items && items.boons) || {};
    const weapon = items && items.weapon;
    const boonIds = Object.keys(boons).filter((id) => OB_ITEMS[id]);
    if ((!weapon || !OB_ITEMS[weapon]) && boonIds.length === 0) return null;
    return (
        <span className="inline-flex flex-wrap items-center gap-1">
            {weapon && OB_ITEMS[weapon] && <ItemPip id={weapon} count={1} />}
            {boonIds.map((id) => (
                <ItemPip key={id} id={id} count={Math.max(1, Math.floor(+boons[id] || 1))} />
            ))}
        </span>
    );
}

OutputBattleItemStrip.propTypes = {
    items: PropTypes.object,
};

export default OutputBattleItemStrip;
